import React, { useState, useEffect, useRef, useId } from 'react';
import { RiMapPinLine } from '@remixicon/react';
import { useAirportSearch } from '../../services/airportService';
import { cn } from '../../../../components/ui/utils';

/**
 * LocationInput Component
 * Airport autocomplete input with search and keyboard navigation
 *
 * @param {Object} props
 * @param {string|Object} props.value - Current value (string or airport object)
 * @param {Function} props.onChange - Callback when value changes
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.error - Error message
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.label - Optional label for the input
 */
const LocationInput = ({
  value = '',
  onChange,
  placeholder = 'Search airport',
  error = '',
  disabled = false,
  label = '',
  icon: Icon = RiMapPinLine
}) => {
  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [debounceTimer, setDebounceTimer] = useState(null);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const errorId = useId();

  // Server-side typeahead: BE `search=` does ILIKE across code, icao,
  // name, city, state, city_code, and country — same coverage as the
  // old client-side filterAirports helper. Only fires when the user has
  // typed 2+ chars (matches BE min-length gate). No more upfront 10K
  // airport fetch just to render an empty dropdown.
  const { data: filteredAirports = [], isLoading: isLoadingAirports, error: airportsError } = useAirportSearch(searchQuery);

  // Initialize input value from prop. Be defensive about missing fields —
  // hydrated airports (e.g. edit mode loading from a quote) may only have
  // the ICAO code without `name`, which would otherwise render as "KOPF - undefined".
  useEffect(() => {
    if (typeof value === 'object' && value !== null) {
      const code = value.icao || value.code || '';
      setInputValue(value.name ? `${code} - ${value.name}` : code);
    } else if (typeof value === 'string') {
      setInputValue(value);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    if (disabled) return;

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // If input is cleared, notify parent
    if (!newValue.trim()) {
      onChange?.(null);
      setSearchQuery('');
      setIsOpen(false);
      return;
    }

    // Set new debounce timer (300ms)
    const timer = setTimeout(() => {
      setSearchQuery(newValue);
      setIsOpen(true);
      setHighlightedIndex(-1);
    }, 300);

    setDebounceTimer(timer);
  };

  // Handle airport selection
  const handleSelectAirport = (airport) => {
    setInputValue(`${airport.icao} - ${airport.name}`);
    setIsOpen(false);
    setSearchQuery('');
    onChange?.(airport);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredAirports.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredAirports.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredAirports.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredAirports.length) {
          handleSelectAirport(filteredAirports[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  return (
    <div className="relative w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-700" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={isOpen ? 'airport-listbox' : undefined}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'w-full pl-9 pr-4 py-2.5 border rounded-lg text-sm transition-all text-gray-700 placeholder-gray-700',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            error && 'error border-red-500 focus:ring-red-500',
            disabled && 'disabled bg-gray-100 cursor-not-allowed opacity-60',
            !error && !disabled && 'border-gray-300 hover:border-gray-400'
          )}
        />
      </div>

      {/* Loading state */}
      {isLoadingAirports && (
        <div
          className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="text-xs text-gray-500">Loading...</span>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent"></div>
        </div>
      )}

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div
          ref={dropdownRef}
          id="airport-listbox"
          role="listbox"
          aria-label="Airport suggestions"
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-auto"
          style={{ maxHeight: '300px' }}
        >
          {isLoadingAirports && (
            <div className="px-4 py-3 text-sm text-gray-500">
              Loading airports...
            </div>
          )}

          {!isLoadingAirports && airportsError && (
            <div className="px-4 py-3 text-sm text-red-600">
              Failed to load airports. Please try again.
            </div>
          )}

          {!isLoadingAirports && !airportsError && filteredAirports.length === 0 && (
            <div className="px-4 py-3 text-sm text-gray-500">
              No airports found
            </div>
          )}

          {!isLoadingAirports && !airportsError && filteredAirports.length > 0 && (
            filteredAirports.map((airport, index) => (
              <div
                key={`${airport.icao}-${index}`}
                role="option"
                aria-selected={index === highlightedIndex ? 'true' : 'false'}
                aria-label={`${airport.icao} - ${airport.name}, ${airport.city}, ${airport.country}`}
                onClick={() => handleSelectAirport(airport)}
                className={cn(
                  'px-4 py-3 cursor-pointer transition-colors',
                  'hover:bg-gray-50',
                  index === highlightedIndex && 'highlighted bg-primary-50'
                )}
              >
                <div className="flex flex-col">
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-gray-900">{airport.icao}</span>
                    <span className="text-sm text-gray-600">{airport.name}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {airport.city}, {airport.country}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div
          id={errorId}
          className="mt-1 text-sm text-red-600"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default LocationInput;
